import { useEffect, useRef, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';

/**
 * useRealtimeBusinesses
 * Subscribes to player_businesses, territory_bonuses, and business revenue ticks.
 */
export function useRealtimeBusinesses({ userId, onBusinessChange, onRevenueEarned, onTerritoryUpdate, enabled = true }) {
  const [status, setStatus] = useState('idle');
  const channelsRef = useRef([]);
  const mountedRef = useRef(true);

  const cleanup = useCallback(() => {
    channelsRef?.current?.forEach(ch => {
      try { supabase?.removeChannel(ch); } catch (e) {}
    });
    channelsRef.current = [];
  }, []);

  const subscribe = useCallback(() => {
    if (!userId || !enabled || !mountedRef?.current) return;
    cleanup();
    setStatus('connecting');

    // 1. player_businesses (INSERT, UPDATE, DELETE)
    const bizChannel = supabase
      ?.channel(`realtime-businesses-${userId}`)
      ?.on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'businesses',
        filter: `user_id=eq.${userId}`,
      }, (payload) => {
        if (!mountedRef?.current) return;
        onBusinessChange?.(payload);
      })
      ?.subscribe((s) => {
        if (!mountedRef?.current) return;
        if (s === 'SUBSCRIBED') setStatus('connected');
        else if (s === 'CHANNEL_ERROR' || s === 'TIMED_OUT') setStatus('error');
      });

    // 2. audit_logs for business revenue ticks (INSERT with action='business_revenue')
    const revenueChannel = supabase
      ?.channel(`realtime-biz-revenue-${userId}`)
      ?.on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'audit_logs',
        filter: `user_id=eq.${userId}`,
      }, (payload) => {
        if (!mountedRef?.current) return;
        const action = payload?.new?.action || '';
        if (action === 'business_revenue' || action === 'revenue_tick') {
          onRevenueEarned?.(payload?.new);
        }
      })
      ?.subscribe();

    // 3. territory_bonuses (any change)
    const territoryChannel = supabase
      ?.channel(`realtime-territory-${userId}`)
      ?.on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'territory_bonuses',
      }, (payload) => {
        if (!mountedRef?.current) return;
        onTerritoryUpdate?.(payload);
      })
      ?.subscribe();

    channelsRef.current = [bizChannel, revenueChannel, territoryChannel]?.filter(Boolean);
  }, [userId, enabled, onBusinessChange, onRevenueEarned, onTerritoryUpdate, cleanup]);

  useEffect(() => {
    mountedRef.current = true;
    if (enabled && userId) subscribe();
    return () => {
      mountedRef.current = false;
      cleanup();
    };
  }, [subscribe, enabled, userId]);

  return { status };
}
